/**
 * The arithmetic under the pager, kept out of the component so it can be tested.
 *
 * There is no DOM in this workspace's test environment, so anything that lives
 * inside a `.tsx` is unreachable by a test. The numbers on a pager are exactly
 * the sort of thing that is wrong by one for months without anybody filing it,
 * so they live here.
 */

/**
 * The rows-per-page choices every table footer offers, and the one it starts on.
 *
 * Twenty, fifty and a hundred, starting at twenty, which is what the reference
 * console measures. A list that wants other sizes still passes its own, and
 * `pageSizeOptions` folds the current one in whatever the list passed.
 */
export const PAGE_SIZES: readonly number[] = [20, 50, 100]
export const DEFAULT_PAGE_SIZE = 20

/**
 * How many pages a list of `total` rows makes. Never fewer than one, because an
 * empty list is still shown on a page, and "Page 1 of 0" is not a sentence.
 */
export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0 || !Number.isFinite(total) || total <= 0) return 1
  return Math.max(1, Math.ceil(total / pageSize))
}

/**
 * A zero-based page index pulled back inside the list.
 *
 * A page number read off the address bar can point past the end, because the
 * list shrank since the link was copied or because somebody typed it. Showing
 * an empty page with a Back button is the wrong answer to both. The last page
 * that exists is the right one.
 */
export function clampPageIndex(pageIndex: number, total: number, pageSize: number): number {
  if (!Number.isFinite(pageIndex) || pageIndex < 0) return 0
  return Math.min(Math.floor(pageIndex), pageCount(total, pageSize) - 1)
}

/**
 * The sizes the rows-per-page picker offers, with the current one guaranteed present.
 *
 * A picker whose value matches none of its rows has nothing honest to draw. The
 * browser's own control showed the first one, so a list opened at 50 against a
 * set of 20, 100 and 200 sat there claiming to be showing 20 rows. The picker
 * the app draws itself goes blank instead, which is a different wrong answer
 * rather than a better one. Folding the current size in costs one line and
 * removes the whole class of that.
 */
export function pageSizeOptions(pageSize: number, sizes: readonly number[]): number[] {
  return [...new Set([...sizes, pageSize])].sort((a, b) => a - b)
}

/**
 * Whether the server said there is anything after the page it just sent.
 *
 * The cursor IS the answer and nothing else is. A page that came back full is
 * not evidence of a next page (the last page can be exactly full), and
 * `loaded < total` is not evidence either, because a total counted a moment
 * before the page was cut can disagree with it under a concurrent write.
 */
export function hasNextPage(cursor: string | null | undefined): boolean {
  return cursor !== null && cursor !== undefined && cursor !== ''
}

/**
 * The human numbering of the rows on screen, one-based and inclusive.
 *
 * `pageIndex * pageSize` is only the offset of the first row while every page
 * before this one was the same size, which is why changing the rows-per-page
 * control has to send somebody back to the first page. `useListPage` does that.
 * Stated here because the arithmetic silently assumes it.
 */
export function pageRange(pageIndex: number, pageSize: number, shown: number): { from: number; to: number } {
  if (shown <= 0) return { from: 0, to: 0 }
  const from = pageIndex * pageSize + 1
  return { from, to: pageIndex * pageSize + shown }
}

/**
 * What the pager says in words.
 *
 * An unknown total is written as a range with no "of", not as a guess. Some
 * lists here answer without one and inventing a denominator from the rows in
 * hand is how a page of fifty starts claiming there are fifty.
 */
export function pageSummary(
  pageIndex: number,
  pageSize: number,
  shown: number,
  total: number | undefined,
  format: (value: number) => string = String,
): string {
  if (shown <= 0) return 'Nothing on this page'
  const { from, to } = pageRange(pageIndex, pageSize, shown)
  const range = `Showing ${format(from)} to ${format(to)}`
  return total === undefined ? range : `${range} of ${format(total)}`
}

/**
 * What to ask for when a list has to be whole.
 *
 * `MAX_PAGE_SIZE` in apps/api/src/lib/http.ts, kept in step by hand because the
 * web app shares no code with the API. Asking for it is not the same as being
 * complete, which is what `shortfallNote` is for: it is the most the server
 * will give in one request, and anything past it has to be said out loud rather
 * than dropped.
 */
export const COMPLETE_LIST_LIMIT = 200

/**
 * What a list that has to be COMPLETE says when it was not.
 *
 * Not every list here is a list somebody pages through. Some are read whole and
 * then counted, grouped or offered as options, and a page of one of those is
 * not a smaller view of it, it is a wrong answer. The integration panel counts
 * tools per account against a ceiling; a page of the sessions makes the count
 * too low and the panel says an account has room it does not have. A picker
 * missing its last option is a thing somebody cannot choose and has no way to
 * discover.
 *
 * The fix for those is to ask for as many as the server will give, and then to
 * say plainly when even that was not all of them. It converts a silent wrong
 * answer into a stated limit, which is the least this can be and still be
 * honest. The alternative, following the cursor until it runs out, is a loop
 * over an unbounded list inside a render path, and that is a worse failure than
 * the one it fixes.
 */
export function shortfallNote(shown: number, total: number, plural: string): string | null {
  if (total <= shown) return null
  return `Showing ${String(shown)} of ${String(total)} ${plural}. This panel reads them in one request, so the rest are not counted here.`
}
